import { describeCondition, type AlertDto, type ClientDto, type MetricDefinition, type RuleDto, type SeriesDto } from '@citilight-monitor/contracts';
import { cn } from '../../../lib/cn';
import { chartPoints, metricView, orderMetrics, type ChartMarker, type ChartThreshold, type MetricView } from '../../../lib/metrics';
import { formatDateTime, formatDuration } from '../../../lib/time';
import { ChartCard } from './ChartCard';
import { ChartSyncProvider } from './chartSync';
import { ReachabilityStrip } from './ReachabilityStrip';

interface MetricChartGridProps {
  client: ClientDto;
  series: SeriesDto | undefined;
  alerts: AlertDto[];
  fromMs: number;
  toMs: number;
  pollIntervalMs: number;
  /** Previous data is still shown while a new range loads. */
  refreshing: boolean;
}

/**
 * Reachability timeline first, then one chart per metric (application metrics full width, server
 * resources in two columns). All share one crosshair; alerts sent are marked on each metric's time axis.
 */
export function MetricChartGrid({ client, series, alerts, fromMs, toMs, pollIntervalMs, refreshing }: MetricChartGridProps) {
  const binMs = series?.binMs ?? pollIntervalMs;
  const resolution =
    binMs > pollIntervalMs
      ? `Average per ${formatDuration(binMs)} · band shows min–max`
      : `Every poll (${formatDuration(pollIntervalMs)})`;
  const ordered = orderMetrics(client.metrics);
  const stateMetrics = ordered.filter((m) => m.unit === 'state');

  return (
    <ChartSyncProvider>
      <div className={cn('grid grid-cols-1 gap-3 lg:grid-cols-2', refreshing && 'opacity-55 transition-opacity')}>
        {stateMetrics.length > 0 && (
          <ReachabilityStrip
            rows={stateMetrics.map((m) => ({ key: m.key, label: m.label, points: chartPoints(series, metricView(m)) }))}
            fromMs={fromMs}
            toMs={toMs}
            binMs={binMs}
          />
        )}
        {ordered
          .filter((m) => m.unit !== 'state')
          .map((definition) => {
            const view = metricView(definition);
            return (
              <ChartCard
                key={definition.key}
                title={view.title}
                subtitle={resolution}
                points={chartPoints(series, view)}
                fromMs={fromMs}
                toMs={toMs}
                binMs={binMs}
                pollIntervalMs={pollIntervalMs}
                formatValue={view.formatValue}
                formatTick={view.formatTick}
                domain={view.domain}
                threshold={thresholdFor(definition, client.rules, view)}
                markers={markersFor(definition, alerts)}
                wide={definition.source === 'app'}
              />
            );
          })}
      </div>
    </ChartSyncProvider>
  );
}

function thresholdFor(definition: MetricDefinition, rules: RuleDto[], view: MetricView): ChartThreshold | undefined {
  const rule = rules.find((r) => r.metric === definition.key);
  if (!rule || definition.unit === 'state') return undefined;
  return {
    value: rule.threshold,
    label: `Alert ${rule.operator.startsWith('<') ? 'below' : 'above'} ${view.formatValue(rule.threshold)}`,
    tone: rule.severity === 'critical' ? 'critical' : 'warning',
  };
}

function markersFor(definition: MetricDefinition, alerts: AlertDto[]): ChartMarker[] {
  return alerts
    .filter((a) => a.metric === definition.key)
    .map((a) => ({
      t: Date.parse(a.sentAt),
      tone: a.severity === 'critical' ? 'critical' : 'warning',
      label: `${a.kind === 'fired' ? 'Alert fired' : 'Reminder sent'} ${formatDateTime(a.sentAt)}: ${describeCondition(
        definition.label,
        definition.unit,
        a.operator,
        a.threshold,
      )}`,
    }));
}
